import type { ReactNode } from "react";

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="state state--loading">{label}</div>;
}
export function ErrorState({ message }: { message: string }) {
  return <div className="state state--error">{message}</div>;
}
export function EmptyState({ children }: { children: ReactNode }) {
  return <div className="state state--empty">{children}</div>;
}

// Standardized page hero — the doctor/patient `.dp-head` treatment (display-font title + muted sub line),
// rendered inline at the top of each page (THU-354). Replaces the experimental shell topbar.
export function PageHeader({
  title,
  sub,
  actions,
}: {
  title: string;
  sub?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="dp-head">
      <div className="dp-head__main">
        <h1 className="dp-head__title">{title}</h1>
        {sub && <div className="dp-head__sub">{sub}</div>}
      </div>
      {actions && <div className="dp-head__actions">{actions}</div>}
    </div>
  );
}

export function KpiCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: "red" | "amber" | "green";
}) {
  return (
    <div className={`kpi ${tone ? `kpi--${tone}` : ""}`}>
      <div className="kpi__value">{value}</div>
      <div className="kpi__label">{label}</div>
      {hint && <div className="kpi__hint">{hint}</div>}
    </div>
  );
}

// Semantic status pill — maps each PHI-free status string to a championcare-design `.tag` tone
// (green/amber/blue/red; "" = neutral tag). Verbatim reference colors via the shared .tag atoms.
const TONE: Record<string, string> = {
  synced: "green",
  active: "green",
  completed: "green",
  approved: "green",
  confirmed: "green",
  paid: "green",
  failed: "red",
  no_show: "red",
  rejected: "red",
  live: "red",
  pending: "amber",
  submitted: "amber",
  in_review: "amber",
  queued: "amber",
  scheduled: "blue",
  cancelled: "",
};

export function StatusBadge({ value }: { value?: string | null }) {
  const v = (value ?? "—").toString();
  const tone = TONE[v] ?? "";
  return <span className={`tag ${tone}`.trim()}>{v.replace(/_/g, " ")}</span>;
}
